import { useHeaderTitle } from '@blend-ed/blendx-platform';
import { Badge, Button, IconButton, Table, useToast } from '@blend-ed/blendx-ui';
import { AppContext } from '@edx/frontend-platform/react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import ActiveTickets from '../../components/ActiveTickets';
import useDeleteTicket from '../../hooks/support/useDeleteTicket';
import useFetchTicket from '../../hooks/support/useFetchTicket';
import useConfirmation from '../../real-hooks/useConfirmation';
import ActionModalSupport from './actionModalSupport';
import InputDrawerSupport from './InputDrawerSupport';

function Support() {
    const { setTitle } = useHeaderTitle()
    const [showDrawer, setShowDrawer] = useState(false)
    const { authenticatedUser } = useContext(AppContext)
    const showToast = useToast()
    const { data: ticketsData, loading, error, refetch } = useFetchTicket(authenticatedUser.id)
    const { deleteTicket, loading: deleteTicketLoading, error: deleteTicketError } = useDeleteTicket()
    const { isOpen, openConfirmation, handleClose, handleConfirm } = useConfirmation()

    const getStatusVariant = (status) => {
        switch (status.toLowerCase()) {
            case 'closed':
                return 'success';
            case 'open':
                return 'error';
            case 'escalated':
                return 'warning';
            default:
                return 'neutral';
        }
    }

    const handleDeleteTicket = (ticketId) => {
        deleteTicket({
            variables: { keyId: ticketId },
            onCompleted: () => {
                refetch()
                showToast('check', 'success', 'Ticket deleted successfully')
            },
            onError: () => {
                showToast('close', 'error', 'Something went wrong')
            }
        })
    }

    const data = useMemo(() => {
        if (ticketsData && ticketsData.length > 0) {
            return ticketsData.map((ticket) => ({
                title: ticket.title,
                description: ticket.description,
                type: ticket.type,
                status: <Badge size="md" variant={getStatusVariant(ticket.status)}>{ticket.status}</Badge>,
                date: new Date(ticket.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                action: <IconButton
                    size="sm"
                    icon="delete-bin-5"
                    iconType="fill"
                    variant="ghost-danger"
                    onClick={() => {
                        openConfirmation().then((result) => {
                            if (result) {
                                handleDeleteTicket(ticket.id)
                            }
                        })
                    }}
                />
            }))
        } else {
            return [];
        }
    }, [ticketsData])

    // set the title of the page
    useEffect(() => {
        setTitle('Support')
    }, [])

    const columns = [
        {
            label: 'Date',
            key: 'date',
        },
        {
            label: 'Title',
            key: 'title',
        },
        {
            label: 'Description',
            key: 'description',
        },
        {
            label: 'Type',
            key: 'type',
        },
        {
            label: 'Status',
            key: 'status',
        },
        {
            label: 'Action',
            key: 'action',
        },
    ]

    return (
        <main className="support">
            <div className="support__header">
                <Button size="xs" iconBefore="questionnaire" iconType="line" variant="primary" onClick={() => setShowDrawer(true)} >
                    Raise a Ticket
                </Button>
            </div>

            {ticketsData && <ActiveTickets tickets={ticketsData} />}

            <h2 className="support__section-title">Ticket History</h2>
            <Table
                columns={columns}
                data={data}
            />
            <ActionModalSupport isOpen={isOpen} handleClose={handleClose} handleConfirm={handleConfirm} />
            <InputDrawerSupport show={showDrawer} setShowDrawer={setShowDrawer} refetch={refetch} />
        </main>
    )
}

export default Support